import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Headphones,
  Globe,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  User,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import httpClient from '../../Api/axios';

function Contact() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '', // This will map to "service" in backend
    message: ''
  });

  const navigate = useNavigate();

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState({
    header: false,
    leftImage: false,
    rightForm: false,
    leftAddress: false,
    rightOfficeImage: false
  });

  const headerRef = useRef(null);
  const leftImageRef = useRef(null);
  const rightFormRef = useRef(null);
  const leftAddressRef = useRef(null);
  const rightOfficeImageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (headerRef.current) {
      headerRef.current.dataset.section = 'header';
      observer.observe(headerRef.current);
    }
    if (leftImageRef.current) {
      leftImageRef.current.dataset.section = 'leftImage';
      observer.observe(leftImageRef.current);
    }
    if (rightFormRef.current) {
      rightFormRef.current.dataset.section = 'rightForm';
      observer.observe(rightFormRef.current);
    }
    if (leftAddressRef.current) {
      leftAddressRef.current.dataset.section = 'leftAddress';
      observer.observe(leftAddressRef.current);
    }
    if (rightOfficeImageRef.current) {
      rightOfficeImageRef.current.dataset.section = 'rightOfficeImage';
      observer.observe(rightOfficeImageRef.current);
    }

    return () => {
      if (headerRef.current) observer.unobserve(headerRef.current);
      if (leftImageRef.current) observer.unobserve(leftImageRef.current);
      if (rightFormRef.current) observer.unobserve(rightFormRef.current);
      if (leftAddressRef.current) observer.unobserve(leftAddressRef.current);
      if (rightOfficeImageRef.current) observer.unobserve(rightOfficeImageRef.current);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormStatus({ submitted: false, error: false, message: '' });

    try {
      // Map form data to backend expected structure
      const enquiryData = {
        name: formData.name,
        email: formData.email,
        company: "", // Not in current form, leaving empty
        phone: formData.phone || "Not provided",
        service: formData.subject || "General Inquiry", // Mapping subject to service
        message: formData.message
      };
      
      // Send request to your backend
      const response = await httpClient.post('/api/enquiry', enquiryData);
      
      if (response.data.success) {
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        // Show success message
        setFormStatus({
          submitted: true,
          error: false,
          message: t('contactPage.form.successMessage') || 'Thank you! Your enquiry has been submitted successfully.'
        });

        // Optionally navigate to thank you page after 2 seconds
        setTimeout(() => {
          navigate('/thank-you');
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Check for specific error types
      let errorMessage = t('contactPage.form.errorMessage') || 'Sorry, there was an error submitting your form. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 400) {
          errorMessage = 'Please fill in all required fields.';
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      }
      
      setFormStatus({
        submitted: false,
        error: true,
        message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden font-poppins" dir="ltr">
      {/* Banner Image */}
      <img
        src="/copy1.jpg"
        alt={t('contactPage.banner.alt')}
        className="w-full h-auto object-cover"
      />

      {/* Background Image for the Entire Content Section - STRONG BLUR */}
      <div
        className="relative min-h-screen"
      >
        {/* Strong blur overlay */}
        <div
          className="absolute inset-0 bg-fixed bg-cover bg-center backdrop-blur-2xl"
          style={{ backgroundImage: 'url("/contttt.avif")', filter: 'blur(10px) brightness(1.0)' }}
        ></div>

        {/* Dark overlay for better contrast */}
        <div className="absolute inset-0 bg-black/40"></div>

        {/* Two Column Layout: Image on Left, Form on Right */}
        <section id="contact-form" className="relative py-12 md:py-16 lg:py-20 z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            {/* Header Text */}
            <div
              ref={headerRef}
              className={`text-center mb-8 md:mb-10 lg:mb-12 transition-all duration-1000 ${isVisible.header
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
                }`}
            >
              <h2 className="text-[30px] font-bold text-white mb-2">
                {t('contactPage.header.title1')} <span className="text-red-600">{t('contactPage.header.title2')}</span>
              </h2>
              <p className="text-[18px] text-gray-200 max-w-2xl mx-auto leading-relaxed">
                {t('contactPage.header.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Left Column: Image - Slides in from LEFT */}
              <div
                ref={leftImageRef}
                className={`flex flex-col justify-center transition-all duration-1000 delay-200 ${isVisible.leftImage
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 -translate-x-20'
                  }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white/20 backdrop-blur-md border-2 border-white/30 flex items-center justify-center">
                  <img
                    src="/contactt.jpeg"
                    alt={t('contactPage.images.contactAlt')}
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* Right Column: Contact Form - Slides in from RIGHT */}
              <div
                ref={rightFormRef}
                className={`flex flex-col justify-center transition-all duration-1000 delay-200 ${isVisible.rightForm
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-20'
                  }`}
              >
                <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6 md:p-8 lg:p-12 border-2 border-white/40 shadow-2xl">
                  {formStatus.message && (
                    <div className={`p-3 md:p-4 rounded-lg mb-4 md:mb-6 ${formStatus.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                      <div className="flex items-center space-x-2 md:space-x-3">
                        {formStatus.error ? (
                          <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
                        ) : (
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                        )}
                        <p className={`text-sm md:text-base ${formStatus.error ? 'text-red-700' : 'text-green-700'}`}>
                          {formStatus.message}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Contact Form */}
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4 md:space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-gray-800 mb-2 font-medium text-base md:text-lg">
                          {t('contactPage.form.labels.name')} *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 md:py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors text-sm md:text-base"
                            placeholder={t('contactPage.form.placeholders.name')}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-gray-800 mb-2 font-medium text-base md:text-lg">
                          {t('contactPage.form.labels.email')} *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 md:py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors text-sm md:text-base"
                            placeholder={t('contactPage.form.placeholders.email')}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-gray-800 mb-2 font-medium text-base md:text-lg">
                          {t('contactPage.form.labels.phone')}
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 md:py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors text-sm md:text-base"
                            placeholder={t('contactPage.form.placeholders.phone')}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-gray-800 mb-2 font-medium text-base md:text-lg">
                          {t('contactPage.form.labels.subject')}
                        </label>
                        <div className="relative">
                          <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                          <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 md:py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors text-sm md:text-base"
                            placeholder={t('contactPage.form.placeholders.subject')}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-800 mb-2 font-medium text-base md:text-lg">
                        {t('contactPage.form.labels.message')} *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="5"
                        className="w-full px-4 py-2 md:py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors resize-none text-sm md:text-base"
                        placeholder={t('contactPage.form.placeholders.message')}
                        required
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 md:py-4 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-base md:text-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 md:space-x-3 group"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm md:text-base">{t('contactPage.form.sending')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="text-sm md:text-base">{t('contactPage.form.sendButton')}</span>
                          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 transform group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* NEW SECTION: Address on Left, Image on Right */}
            <div className="mt-16 md:mt-20 lg:mt-24">
              {/* "Meet us at our office" Header */}
              <div className="text-center mb-12">
                <h2 className="text-[32px] md:text-[36px] font-bold text-white">
                  {t('contactPage.office.title1')} <span className="text-red-600">{t('contactPage.office.title2')}</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                {/* Left Column: Address Information - Slides in from LEFT */}
                <div
                  ref={leftAddressRef}
                  className={`space-y-12 transition-all duration-1000 delay-200 ${isVisible.leftAddress
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-20'
                    }`}
                >
                  {/* Head Office Section */}
                  <div>
                    <div className="flex items-center mb-6">
                      <h3 className="text-[26px] font-bold text-white">{t('contactPage.office.canadaTitle')}</h3>
                    </div>
                    <div className="pl-7 space-y-6">
                      <div className="flex items-start space-x-4">
                        <p className="text-gray-200 text-lg leading-relaxed">
                          {t('contactPage.office.canadaAddress.line1')}<br />
                          {t('contactPage.office.canadaAddress.line2')}<br />
                          {t('contactPage.office.canadaAddress.line3')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Regional Office Section */}
                  <div>
                    <div className="flex items-center mb-6">
                      <h3 className="text-[26px] font-bold text-white">{t('contactPage.office.indiaTitle')}</h3>
                    </div>
                    <div className="pl-7 space-y-6">
                      <div className="flex items-start space-x-4">
                        <p className="text-gray-200 text-lg leading-relaxed">
                          {t('contactPage.office.indiaAddress.line1')}<br />
                          {t('contactPage.office.indiaAddress.line2')}<br />
                          {t('contactPage.office.indiaAddress.line3')}<br />
                          {t('contactPage.office.indiaAddress.line4')}<br />
                          {t('contactPage.office.indiaAddress.line5')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Image - Slides in from RIGHT */}
                <div
                  ref={rightOfficeImageRef}
                  className={`flex flex-col justify-center transition-all duration-1000 delay-200 ${isVisible.rightOfficeImage
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 translate-x-20'
                    }`}
                >
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white/20 backdrop-blur-md border-2 border-white/30 flex items-center justify-center">
                    <img
                      src="/office_meeting.png"
                      alt={t('contactPage.images.officeAlt')}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Add custom CSS for smooth transitions */}
      <style jsx>{`
        /* Smooth scrolling for the whole page */
        html {
          scroll-behavior: smooth;
        }
        
        /* Smooth transitions for all elements */
        * {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Form success styling */
        .form-success {
          animation: fadeIn 0.5s ease-in;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default Contact;